import { CommonTabbarContext, SystemContext, TabContext } from "@/App";
import SideTool from "@/global/components/side-tool";
import { useContext, useEffect, useRef, useState } from "react";
import "./index.less"
import { useLocation, useParams } from "react-router-dom";
import classNames from "classnames";
import { DEVICE_TYPE, DeviceTypeConfig, getDeviceCnName } from "@/enums/device-type";
import { ReactComponent as ShowSvg } from '@/global/icons/device/show.svg';
import { TAB_TYPE } from "@/enums/tab-type";
import { CommonTabbarState, TabRoute } from "@/global/components/common-tabbar";
import { getGlobalDevices } from "@/api/lib/lib-device";
import { Button, Form, Input, InputNumber, Modal, Select, Skeleton, Spin } from "antd";
import MonixModal from "@/global/components/monix-modal";
import { DATA_BOX_TYPE, DATA_BOX_TYPE_LIST, getDataBoxTypeName } from "@/enums/data-box-type";
import TnMessage from "@/global/components/message";
import TnModal from "@/global/components/base-modal";
import { SystemStatus } from "@/global/store/system-store";
import { SENSOR_TYPE, SENSOR_TYPE_LIST, getSensorTypeName } from "@/enums/sensor-type";
import { SIGNAL_TYPE, SIGNAL_TYPE_LIST, getSignalTypeName } from "@/enums/signal-type";
import { VERIFY_TYPE_LIST } from "@/enums/verify-type";
import { getGlobalMonitorParameters } from "@/api/lib/lib-monitor-parameter";
import { GlobalMonitorParameter } from "@/global/interface/lib-device/global-monitor-parameter";

export default function Device() {
    const addBtnRef = useRef(null); // 添加表单的ref
    const editBtnRef = useRef(null); // 编辑表单的ref
    const [addForm] = Form.useForm(); // 添加表单
    const [editForm] = Form.useForm(); // 编辑表单
    const location = useLocation(); // 路由
    const [loading, setLoading] = useState<boolean>(false) // 侧边栏loading
    const commonTabbarContext = useContext<CommonTabbarState>(CommonTabbarContext); // tabbar上下文
    const systemContext = useContext<SystemStatus>(SystemContext); // system上下文
    const [activeItem, setActiveItem] = useState<{ id: number, type: string }>(null) // 当前激活设备
    const [deviceListMap, setDeviceListMap] = useState<{ [key: string]: any[] }>({}) // 设备列表映射表
    const [showAddModel, setShowAddModel] = useState<boolean>(false) // 添加弹窗标志
    const [addLoading, setAddLoading] = useState<boolean>(false) // 添加弹窗loading
    const [showEditModel, setShowEditModel] = useState<boolean>(false) // 编辑弹窗标志
    const [editLoading, setEditLoading] = useState<boolean>(false) // 编辑弹窗loading
    const [currentDeviceType, setCurrentDeviceType] = useState<string>('') // 当前设备类型
    const [globalMonitorParameters, setGlobalMonitorParameters] = useState<GlobalMonitorParameter[]>([]) // 全局监测参数

    // 初始化设备信息
    useEffect(() => {
        generateData()
    }, [])

    // 监听路由，高亮左侧列表
    useEffect(() => {
        let pathName = location.pathname;

        const match = pathName.match(/\/bim\/project\/\d+\/lib-device\/(\d+)\/(\w+)/);
        if (match) {
            const id = match[1];
            const type = match[2];
            setActiveItem({ id: +id, type: type })
        } else {
            setActiveItem(null);
        }
    }, [location.pathname])

    // 监听弹窗变化，重置表单内容
    useEffect(() => {
        addForm.resetFields();
        setCurrentDeviceType("");
    }, [showAddModel])

    // 监听弹窗变化，重置表单内容
    useEffect(() => {
        if (!showEditModel) {
            editForm.resetFields();
            setCurrentDeviceType("");
        }
    }, [showEditModel])

    // 点击左侧列表项
    const onClickItem = (id: number, type: string, name: string) => {
        commonTabbarContext.navigate(`${TAB_TYPE.LIB_DEVICE}/${id}/${type}`)
    }

    // 获取列表
    const generateData = () => {
        setLoading(true);
        getGlobalDevices().then(res => {
            let deviceMap = res?.data;
            setDeviceListMap({ ...deviceMap })
        }).catch(err => {
            TnMessage.error("服务器错误");
        }).finally(() => {
            setLoading(false);
        })
    }

    // 获取全局监测参数
    const generateGParameter = () => {
        getGlobalMonitorParameters().then(res => {
            if (res.status === 200) {
                let data: GlobalMonitorParameter[] = res.data || [];
                setGlobalMonitorParameters(data);
            }
        })
    }

    // 点击工具栏添加按钮
    const onAddClick = () => {
        setShowAddModel(true);
        generateGParameter();
    }

    // 点击工具栏编辑按钮
    const onEditClick = () => {
        if (!activeItem) {
            TnMessage.warning("未选中设备");
            return;
        }
        // 将信息注入
        const config = DeviceTypeConfig[activeItem?.type];
        if (!config) {
            TnMessage.warning("找不到设备");
            return;
        }
        setShowEditModel(true);
        setCurrentDeviceType(activeItem?.type);
        setEditLoading(true);
        generateGParameter();
        config.findDetailFunction(+activeItem?.id).then(res => {
            let data = res.data;
            editForm.setFieldsValue({ ...data });
        }).catch(err => {
            TnMessage.error("获取设备失败");
        }).finally(() => {
            setEditLoading(false);
        })
    }

    // 点击工具栏删除按钮
    const onDeleteClick = () => {
        if (!activeItem) {
            TnMessage.warning("未选中设备");
            return;
        }
        TnModal().deleteModal({
            onOk() {
                const config = DeviceTypeConfig[activeItem?.type];
                if (config) {
                    config.deleteFunction(activeItem?.id).then(res => {
                        if (res.status === 200) {
                            let idx = deviceListMap[activeItem?.type]?.findIndex(item => item.id === activeItem?.id);
                            deviceListMap[activeItem?.type].splice(idx, 1);
                            setDeviceListMap({ ...deviceListMap });
                            TnMessage.success("删除成功");
                            setShowEditModel(false);
                            Modal.destroyAll();
                            // 删除当前打开的页签
                            let routeIdx = commonTabbarContext.routes?.findIndex(item => item.url === `device/${activeItem?.id}/${activeItem?.type}`)
                            let newRoutes = commonTabbarContext.routes?.filter(item => item.url !== `device/${activeItem?.id}/${activeItem?.type}`)
                            commonTabbarContext.navigate(commonTabbarContext.routes[routeIdx - 1]?.url);
                            commonTabbarContext.setRoutesOverwrite(newRoutes);
                        }
                    }).catch(err => {
                        TnMessage.error("删除失败");
                    })
                }
            },
            onCancel() {
                Modal.destroyAll();
            },
            closable: true,
        })
    }

    // 点击添加弹窗确认按钮
    const onAddSaveClick = () => {
        if (addLoading) return;
        if (addBtnRef.current) {
            addBtnRef.current.click();
        }
    }

    // 创建表单提交
    const onAddFinish = (values) => {
        let { deviceType } = values;
        const config = DeviceTypeConfig[deviceType];

        if (config) {
            setAddLoading(true);
            let data = { ...values };
            if (deviceType === DEVICE_TYPE.SENSOR) {
                data = {
                    ...data,
                    type: values.sensorType
                }
            }
            config.createFunction(data).then(res => {
                if (res.status === 200) {
                    const id = res.data;
                    data = { ...data, id };
                    deviceListMap[deviceType].push(data);
                    setDeviceListMap({ ...deviceListMap });
                    TnMessage.success("添加成功");
                    setShowAddModel(false);
                }
            }).catch(err => {
                TnMessage.error(err.data || '添加失败');
            }).finally(() => {
                setAddLoading(false);
            });
        }
    }

    // 点击编辑弹窗确认按钮
    const onEditSaveClick = () => {
        if (editLoading) return;
        if (editBtnRef.current) {
            editBtnRef.current.click();
        }
    }

    // 编辑表单提交
    const onEditFinish = (values) => {
        const config = DeviceTypeConfig[activeItem?.type];

        if (config) {
            setEditLoading(true);
            let data = { ...values };
            if (activeItem?.type === DEVICE_TYPE.SENSOR) {
                data = {
                    ...data,
                    type: values.sensorType
                }
            }
            config.updateFunction(activeItem?.id, data).then(res => {
                if (res.status === 200) {
                    let idx = deviceListMap[activeItem?.type]?.findIndex(item => item.id === activeItem?.id);
                    deviceListMap[activeItem?.type][idx] = {
                        ...deviceListMap[activeItem?.type][idx],
                        ...data,
                    }
                    setDeviceListMap({ ...deviceListMap });
                    TnMessage.success("编辑成功");
                    setShowEditModel(false);
                    // 同步修改当前页签的内容
                    let routeIdx = commonTabbarContext.routes?.findIndex(item => item.url === `device/${activeItem?.id}/${activeItem?.type}`)
                    commonTabbarContext.routes[routeIdx].title = data?.name || '';
                    commonTabbarContext.setRoutesOverwrite([...commonTabbarContext.routes]);
                    systemContext.needRenderTab();
                }
            }).catch(err => {
                TnMessage.error(err?.data || "编辑失败");
            }).finally(() => {
                setEditLoading(false);
            });
        }
    }

    return (
        <div className="device-side-main">
            <SideTool onAddClick={onAddClick} onEditClick={onEditClick} onDeleteClick={onDeleteClick} />
            <Skeleton loading={loading}>
                <div className="device-body">
                    {Object.keys(deviceListMap)?.map((key, index) => (
                        <div className="list" key={index}>
                            <div className="list-title">
                                {getDeviceCnName(key)}
                            </div>
                            {deviceListMap[key].map((listItem, listKey) => (
                                <div
                                    className={classNames({
                                        "list-item": true,
                                        "list-item-active": activeItem?.id == listItem?.id && activeItem?.type == key
                                    })}
                                    key={listKey}
                                    onClick={() => { onClickItem(listItem.id, key, listItem.name) }}
                                >
                                    <div>
                                        {listItem?.name}
                                    </div>
                                    {activeItem?.id == listItem?.id && activeItem?.type == key && (
                                        <ShowSvg className="icon" />
                                    )}
                                </div>
                            ))}

                        </div>
                    ))}
                </div>
            </Skeleton>


            <MonixModal
                title={"创建设备"}
                visible={showAddModel}
                setVisible={(e) => setShowAddModel(e)}
                onSaveClick={onAddSaveClick}
                wrapClassName="form-modal"
                saveDisable={addLoading}
            >
                <Spin wrapperClassName="form-body" spinning={addLoading}>
                    <Form
                        colon={false}
                        form={addForm}
                        autoComplete="off"
                        labelCol={{ span: 6 }}
                        onFinish={onAddFinish}
                        initialValues={{
                            autoPush: false,
                            sensorStandalone: false,
                        }}
                    >
                        <Form.Item label="设备类型:" name="deviceType" rules={[{ required: true, message: '设备类型不能为空' }]}>
                            <Select onChange={e => setCurrentDeviceType(e)}>
                                <Select.Option value={DEVICE_TYPE.SERVER}>服务器</Select.Option>
                                <Select.Option value={DEVICE_TYPE.DATA_BOX}>采集箱</Select.Option>
                                <Select.Option value={DEVICE_TYPE.SENSOR}>传感器</Select.Option>
                            </Select>
                        </Form.Item>
                        {currentDeviceType === DEVICE_TYPE.SENSOR && <>
                            <Form.Item label="传感器类型:" name="sensorType" rules={[{ required: true, message: '传感器类型不能为空' }]}>
                                <Select>
                                    {SENSOR_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>}
                        {currentDeviceType === DEVICE_TYPE.DATA_BOX && <>
                            <Form.Item label="采集设备类型:" name="type" rules={[{ required: true, message: '采集设备类型不能为空' }]}>
                                <Select>
                                    {DATA_BOX_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>}
                        <Form.Item label="名称:" name="name" rules={[{ required: true, message: '名称不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="厂商:" name="manufacturer" rules={[{ required: true, message: '厂商不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="型号:" name="marking" rules={[{ required: true, message: '型号不能为空' }]}>
                            <Input />
                        </Form.Item>
                        {currentDeviceType === DEVICE_TYPE.DATA_BOX && <>
                            <Form.Item label="通道数量:" name="channelCount" rules={[{ required: true, message: '通道数量不能为空' }]}>
                                <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                            <Form.Item label="信号类型:" name="signalType" rules={[{ required: true, message: '信号类型不能为空' }]}>
                                <Select>
                                    {SIGNAL_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="自动推送数据:" name="autoPush" rules={[{ required: true, message: '自动推送数据不能为空' }]}>
                                <Select defaultValue={false}>
                                    <Select.Option value={true}>是</Select.Option>
                                    <Select.Option value={false}>否</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item label="独立传感器:" name="sensorStandalone" rules={[{ required: true, message: '独立传感器不能为空' }]}>
                                <Select defaultValue={false}>
                                    <Select.Option value={true}>是</Select.Option>
                                    <Select.Option value={false}>否</Select.Option>
                                </Select>
                            </Form.Item>
                        </>}
                        {currentDeviceType === DEVICE_TYPE.SENSOR && <>
                            <Form.Item label="精度:" name="precision" rules={[{ required: true, message: '精度不能为空' }]}>
                                <InputNumber style={{ width: '100%' }} min={0} precision={0} />
                            </Form.Item>
                            <Form.Item label="量程:" name="range" rules={[{ required: true, message: '量程不能为空' }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item label="信号类型:" name="signalType" rules={[{ required: true, message: '信号类型不能为空' }]}>
                                <Select>
                                    {SIGNAL_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="验证类型:" name="verifyType">
                                <Select>
                                    {VERIFY_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="英文名称:" name="englishName">
                                <Input />
                            </Form.Item>
                            <Form.Item label="计算公式:" name="formulaStr">
                                <Input />
                            </Form.Item>
                            <Form.Item label="监测参数:" name="globalParameterIds">
                                <Select mode="tags">
                                    {globalMonitorParameters?.map(item => (
                                        <Select.Option key={item.id} value={item.id}>{item?.name || ''}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>}
                        <Button ref={addBtnRef} htmlType="submit" style={{ display: 'none' }}></Button>
                    </Form>
                </Spin>
            </MonixModal>

            <MonixModal
                title={"编辑设备"}
                visible={showEditModel}
                setVisible={(e) => setShowEditModel(e)}
                onSaveClick={onEditSaveClick}
                wrapClassName="form-modal"
                saveDisable={editLoading}
            >
                <Spin wrapperClassName="form-body" spinning={editLoading}>
                    <Form
                        colon={false}
                        form={editForm}
                        autoComplete="off"
                        labelCol={{ span: 6 }}
                        onFinish={onEditFinish}
                    >
                        <Form.Item label="名称:" name="name" rules={[{ required: true, message: '名称不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="厂商:" name="manufacturer" rules={[{ required: true, message: '厂商不能为空' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="型号:" name="marking" rules={[{ required: true, message: '型号不能为空' }]}>
                            <Input />
                        </Form.Item>
                        {currentDeviceType === DEVICE_TYPE.DATA_BOX && <>
                            <Form.Item label="通道数量:" name="channelCount" rules={[{ required: true, message: '通道数量不能为空' }]}>
                                <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                            <Form.Item label="信号类型:" name="signalType" rules={[{ required: true, message: '信号类型不能为空' }]}>
                                <Select>
                                    {SIGNAL_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="自动推送数据:" name="autoPush" rules={[{ required: true, message: '自动推送数据不能为空' }]}>
                                <Select>
                                    <Select.Option value={true}>是</Select.Option>
                                    <Select.Option value={false}>否</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item label="独立传感器:" name="sensorStandalone" rules={[{ required: true, message: '独立传感器不能为空' }]}>
                                <Select>
                                    <Select.Option value={true}>是</Select.Option>
                                    <Select.Option value={false}>否</Select.Option>
                                </Select>
                            </Form.Item>
                        </>}
                        {currentDeviceType === DEVICE_TYPE.SENSOR && <>
                            <Form.Item label="精度:" name="precision" rules={[{ required: true, message: '精度不能为空' }]}>
                                <InputNumber style={{ width: '100%' }} min={0} precision={0} />
                            </Form.Item>
                            <Form.Item label="量程:" name="range" rules={[{ required: true, message: '量程不能为空' }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item label="信号类型:" name="signalType" rules={[{ required: true, message: '信号类型不能为空' }]}>
                                <Select>
                                    {SIGNAL_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="验证类型:" name="verifyType" rules={[{ required: true, message: '验证类型不能为空' }]}>
                                <Select>
                                    {VERIFY_TYPE_LIST.map(item => (
                                        <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="英文名称:" name="englishName">
                                <Input />
                            </Form.Item>
                            <Form.Item label="计算公式:" name="formulaStr">
                                <Input />
                            </Form.Item>
                            <Form.Item label="监测参数:" name="globalParameterIds">
                                <Select mode="tags">
                                    {globalMonitorParameters?.map(item => (
                                        <Select.Option key={item.id} value={item.id}>{item?.name || ''}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>}
                        <Button ref={editBtnRef} htmlType="submit" style={{ display: 'none' }}></Button>
                    </Form>
                </Spin>
            </MonixModal>
        </div>
    )
}